import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, RefreshCw, X } from 'lucide-react';
import { useTenantsSearch, useTenantById } from '@/shared/hooks/useTenantsSearch.ts';

interface Props {
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export function TenantCombobox({
  value,
  onChange,
  placeholder = 'Todos os tenants',
  required = false,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  const [selectedLabel, setSelectedLabel] = useState('');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const { data: results = [], isLoading, isFetching } = useTenantsSearch(search);

  // Resolve name when value is set externally and we don't have a label
  const needsResolve = !!value && !selectedLabel;
  const { data: resolved } = useTenantById(needsResolve ? value : undefined);

  useEffect(() => {
    if (resolved && needsResolve) {
      setSelectedLabel(resolved.nome);
    }
  }, [resolved, needsResolve]);

  // Reset label when value is cleared externally
  useEffect(() => {
    if (!value) setSelectedLabel('');
  }, [value]);

  // Build options list
  const options = [
    ...(!required ? [{ id: '', nome: 'Todos os tenants', slug: '' }] : []),
    ...results,
  ];

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 320;
    const top = spaceBelow >= dropdownHeight ? rect.bottom + 4 : rect.top - dropdownHeight - 4;
    setPos({ top, left: rect.left, width: rect.width });
  }, []);

  // Open/close handlers
  function handleOpen() {
    if (disabled) return;
    setOpen(true);
    setSearch('');
    setActiveIdx(-1);
    requestAnimationFrame(() => {
      updatePos();
      inputRef.current?.focus();
    });
  }

  function handleClose() {
    setOpen(false);
    setSearch('');
  }

  function handleSelect(id: string, nome: string) {
    onChange(id);
    setSelectedLabel(id ? nome : '');
    handleClose();
  }

  // Click outside + scroll close
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) return;
      handleClose();
    }
    function onScroll() { handleClose(); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  });

  // Keyboard navigation
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      handleClose();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => Math.min(prev + 1, options.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => Math.max(prev - 1, 0));
    }
    if (e.key === 'Enter' && activeIdx >= 0 && activeIdx < options.length) {
      e.preventDefault();
      const opt = options[activeIdx];
      handleSelect(opt.id, opt.nome);
    }
  }

  // Scroll active item into view
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    const list = listRef.current;
    if (!list) return;
    const item = list.children[activeIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const displayText = selectedLabel || placeholder;
  const hasValue = !!value;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? handleClose() : handleOpen())}
        disabled={disabled}
        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
          disabled
            ? 'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500'
            : 'border-gray-300 bg-white hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-gray-500'
        } ${!hasValue ? 'text-gray-500 dark:text-gray-400' : ''}`}
      >
        <span className="truncate">{displayText}</span>
        <div className="flex items-center gap-1">
          {hasValue && !required && (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect('', '');
              }}
              className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
            style={{ top: pos.top, left: pos.left, width: Math.max(pos.width, 400), maxHeight: 320 }}
            onKeyDown={handleKeyDown}
          >
            {/* Search input */}
            <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
              <Search size={16} className="flex-shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setActiveIdx(-1); }}
                placeholder="Buscar tenant..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400 dark:text-gray-100"
              />
              {isFetching && <RefreshCw size={14} className="flex-shrink-0 animate-spin text-gray-400" />}
            </div>

            {/* Options list */}
            <div ref={listRef} className="flex-1 overflow-y-auto" style={{ maxHeight: 264 }}>
              {isLoading && results.length === 0 ? (
                <div className="flex items-center justify-center py-6 text-sm text-gray-400">
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Carregando...
                </div>
              ) : options.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400">
                  Nenhum tenant encontrado
                </div>
              ) : (
                options.map((opt, idx) => {
                  const isSelected = opt.id === value;
                  const isActive = idx === activeIdx;
                  return (
                    <button
                      key={opt.id || '__all__'}
                      type="button"
                      onClick={() => handleSelect(opt.id, opt.nome)}
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : isActive
                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{opt.nome}</span>
                        {opt.slug && (
                          <span className="block text-xs text-gray-400">{opt.slug}.woli.com.br</span>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
